import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Icon } from "@iconify-icon/react";
import { motion } from "motion/react";

import { disableReservations } from "../../api/workspaceSettings";
import { useWorkspaceSettingsStore } from "../../store/useWorkspaceSettingsStore";
import useWorkspaceInformationStore from "../../store/useWorkspaceInformationStore";
import { createDisableReservationsSchema, DisableReservationsFormData } from "../../schemas";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";

interface DisableReservationsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DisableReservationsDialog = ({ isOpen, onClose }: DisableReservationsDialogProps) => {
  const { t } = useTranslation("workspaces");
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { setSettings, settings } = useWorkspaceSettingsStore();
  const { refetch: refetchWorkspaceInfo } = useWorkspaceInformationStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reasonLength, setReasonLength] = useState(0);
  const disableReservationsSchema = createDisableReservationsSchema(t);

  const form = useForm<DisableReservationsFormData>({
    resolver: zodResolver(disableReservationsSchema),
    defaultValues: {
      reason: "",
    },
  });

  const handleSubmit = async (data: DisableReservationsFormData) => {
    if (!workspaceId) return;

    setIsSubmitting(true);
    try {
      await disableReservations(workspaceId, data);

      if (settings) {
        setSettings({
          ...settings,
          user_status: {
            ...settings.user_status,
            has_reservations_disabled: true,
            reservations_disabled_reason: data.reason,
            reservations_disabled_at: new Date().toISOString(),
          },
        });
      }

      form.reset();
      setReasonLength(0);
      onClose();

      // Refetch workspace basics data
      await refetchWorkspaceInfo(workspaceId);
    } catch (error: any) {
      // Error is handled by form validation and displayed in the dialog
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      form.reset();
      setReasonLength(0);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-background/95 border-border/50 h-fit max-h-dvh max-w-2xl p-0 shadow-2xl backdrop-blur-xl">
        {/* Background Pattern */}
        <div className="absolute inset-0 overflow-hidden rounded-xl opacity-10">
          <div className="absolute -top-20 -right-24 h-48 w-48 rounded-full bg-gradient-to-br from-red-500 to-orange-500" />
          <div className="absolute -bottom-24 -left-20 h-56 w-56 rounded-full bg-gradient-to-tr from-red-600 to-pink-500" />
        </div>

        <ScrollArea className="h-full max-h-dvh">
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className="relative p-6"
            initial={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <DialogHeader className="pb-6">
              <motion.div
                animate={{ scale: 1 }}
                className="mx-auto mb-4 flex size-16 items-center justify-center"
                initial={{ scale: 0 }}
                transition={{ duration: 0.4, type: "spring", stiffness: 200 }}
              >
                <div className="flex size-full items-center justify-center rounded-full border-2 border-red-500/30 bg-red-500/20 backdrop-blur-sm">
                  <Icon
                    className="text-red-500"
                    height="1.5rem"
                    icon="mdi:pause-circle"
                    width="1.5rem"
                  />
                </div>
              </motion.div>
              <DialogTitle className="text-accent-foreground text-center text-2xl font-bold">
                {t("settings.reservations.disableDialog.title")}
              </DialogTitle>
              <div className="text-muted-foreground text-center text-sm">
                {t("settings.reservations.disableDialog.description")}
              </div>
            </DialogHeader>

            <div className="space-y-6">
              <Form {...form}>
                <form className="space-y-6" onSubmit={form.handleSubmit(handleSubmit)}>
                  <FormField
                    control={form.control}
                    name="reason"
                    render={({ field }) => (
                      <div>
                        <FormItem className="space-y-3">
                          <div className="flex items-center justify-between">
                            <FormLabel className="text-accent-foreground flex items-center gap-2 text-lg font-medium">
                              <Icon height={"1.25rem"} icon="mdi:text-box" width={"1.25rem"} />
                              {t("settings.reservations.disableDialog.reasonLabel")}
                            </FormLabel>
                            <span className="text-muted-foreground text-sm">
                              {reasonLength}/{500}
                            </span>
                          </div>
                          <FormControl>
                            <Card className="border-input overflow-hidden rounded-xl border shadow-sm">
                              <CardContent className="p-0">
                                <ScrollArea className="border-input bg-background ring-offset-background focus-within:ring-ring h-32 w-full rounded-xl">
                                  <Textarea
                                    className="[field-sizing:content] min-h-[128px] resize-none rounded-xl border-none! p-4 text-base break-words whitespace-pre-wrap focus-visible:ring-0 focus-visible:ring-offset-0"
                                    placeholder={t(
                                      "settings.reservations.disableDialog.reasonPlaceholder",
                                    )}
                                    {...field}
                                    maxLength={500}
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                      setReasonLength(e.target.value.length);
                                    }}
                                  />
                                </ScrollArea>
                              </CardContent>
                            </Card>
                          </FormControl>
                        </FormItem>
                      </div>
                    )}
                  />

                  {/* Action buttons */}
                  <div className="flex justify-center gap-4 pt-6">
                    <Button
                      className="bg-muted/80 hover:bg-muted text-muted-foreground hover:text-accent-foreground min-w-32 rounded-xl"
                      disabled={isSubmitting}
                      type="button"
                      variant="ghost"
                      onClick={handleClose}
                    >
                      {t("settings.reservations.disableDialog.cancel")}
                    </Button>
                    <Button
                      className="min-w-32 gap-2 rounded-xl bg-red-500 text-white hover:bg-red-600"
                      disabled={isSubmitting}
                      type="submit"
                    >
                      {isSubmitting ? (
                        <>
                          <Icon className="animate-spin" icon="lucide:loader-2" />
                          {t("settings.reservations.disableDialog.disabling")}
                        </>
                      ) : (
                        <>
                          <Icon icon="mdi:pause-circle" />
                          {t("settings.reservations.disableDialog.confirm")}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </motion.div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
